import { Cancel01Icon } from '@hugeicons/core-free-icons';
import * as Linking from 'expo-linking';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { LogoIcon } from '../assets/images';
import { usePushNotifications } from '../contexts/usePushNotifications';
import { useMediaQueries } from '../utils/responsivity';
import type { ButtonProps } from './Button';
import { Button, ButtonVariants } from './Button';
import { Header } from './Header';
import { HeaderButton } from './HeaderButton';
import { Loader } from './Loader';
import type { ModalProps } from './Modal';
import { Modal } from './Modal';
import { PageHeader } from './PageHeader';
import { TextReg } from './StyledText';

type Props = Omit<ButtonProps, 'children'> &
  Pick<ModalProps, 'hide' | 'visible'>;

export function EnableNotificationsModal({
  visible,
  hide,
  ...buttonProps
}: Props) {
  const { permission } = usePushNotifications();
  const { isMobile } = useMediaQueries();

  const modalHeader = isMobile ? (
    <Header
      headerRight={<HeaderButton icon={Cancel01Icon} onPress={hide} />}
      mobileLogo={LogoIcon}
    />
  ) : null;

  if (!permission) {
    return (
      <Modal visible={visible} hide={hide} header={modalHeader}>
        <Loader />
      </Modal>
    );
  }

  if (!permission?.granted && !permission?.canAskAgain) {
    return (
      <Modal visible={visible} hide={hide} header={modalHeader}>
        <View style={styles.container}>
          <PageHeader
            header="Oh no!"
            subheader="We rely on push notifications to provide you with reminders for events. Please go to your settings and enable them."
          />
          <Button
            variant={ButtonVariants.PURPLE}
            onPress={Linking.openSettings}
          >
            <TextReg>Open Settings</TextReg>
          </Button>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} hide={hide} header={modalHeader}>
      <View style={styles.container}>
        <PageHeader
          header={permission.granted ? "You're good to go!" : "Can't wait!"}
          subheader={
            permission.granted
              ? 'Push notifications are already enabled.'
              : 'Enable push notifications to get reminders when this event is about to start.'
          }
        />
        <TextReg style={styles.emoji}>&#128588;</TextReg>
        <Button variant={ButtonVariants.PURPLE} {...buttonProps}>
          <TextReg>{permission.granted ? 'Next' : "Let's do it"}</TextReg>
        </Button>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    width: '100%',
  },
  emoji: {
    fontSize: 125,
  },
});
